import React, { useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import { useSelector } from "react-redux";
import Loading from "../components/Loading";
import api from "../api";
import { getDealStatusLabel } from "../../constants/filters";

// Error Boundary Component for Dashboard
class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {}

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
          <h3 className="text-red-800 font-semibold">Dashboard Error</h3>
          <p className="text-red-600">
            There was an error rendering the dashboard. Please refresh the page.
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-red-700">
              Error Details
            </summary>
            <pre className="text-xs text-red-600 mt-2 overflow-auto">
              {this.state.error?.toString()}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// Dashboard component renders various sections of the dashboard using the API data.
const Dashboard = ({ data }) => {
  if (!data) return null;

  // Helper function to safely render values
  const safeRender = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") {
      // If it's an object, try to extract meaningful information
      if (value.hasOwnProperty("count") && value.hasOwnProperty("totalValue")) {
        return `Count: ${value.count}, Total: ${value.totalValue}`;
      }
      if (value.hasOwnProperty("_id") && value.hasOwnProperty("count")) {
        return `${value._id}: ${value.count}`;
      }
      if (value.hasOwnProperty("avgValue")) {
        return `Avg: ${value.avgValue}`;
      }
      // For any other object, try to render its string representation
      try {
        return JSON.stringify(value);
      } catch (e) {
        return "[Object]";
      }
    }
    return String(value);
  };

  // KPIs Section - Enhanced with new metrics
  const kpis = [
    { label: "Total Leads", value: data.kpis?.totalLeads },
    { label: "Hot Leads", value: data.kpis?.hotLeads },
    { label: "Converted Leads", value: data.kpis?.convertedLeads },
    {
      label: "Conversion Rate",
      value: data.kpis?.conversionRate ? `${data.kpis.conversionRate}%` : "0%",
    },
    {
      label: "Contact Rate",
      value: data.kpis?.contactRate
        ? `${data.kpis.contactRate.toFixed(1)}%`
        : "0%",
    },
    {
      label: "Avg Response Time",
      value: data.kpis?.avgResponseTime
        ? `${data.kpis.avgResponseTime.toFixed(2)} days`
        : "-",
    },
    {
      label: "Potential Revenue",
      value: data.kpis?.potentialRevenue
        ? `$${(data.kpis.potentialRevenue / 1000000).toFixed(1)}M`
        : "-",
    },
    {
      label: "Lead Quality",
      value: data.kpis?.leadQuality ? `${data.kpis.leadQuality}%` : "-",
    },
  ];
  return (
    <div>
      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="bg-white shadow rounded-lg p-4">
            <h2 className="text-sm font-medium text-gray-600">{kpi.label}</h2>
            <p className="text-xl font-bold text-gray-900">
              {safeRender(kpi.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Recent Leads Section */}
      {Array.isArray(data.kpis?.recentLeads) &&
        data.kpis.recentLeads.length > 0 && (
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <h2 className="text-xl font-bold mb-4">Recent Leads</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Email
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Company
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Priority
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Score
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Potential Savings
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                      Days Ago
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.kpis.recentLeads.map((lead, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">
                        {safeRender(lead.name)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {safeRender(lead.email)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {safeRender(lead.company)}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${
                            lead.priorityColor === "purple"
                              ? "bg-purple-100 text-purple-800"
                              : lead.priorityColor === "red"
                              ? "bg-red-100 text-red-800"
                              : lead.priorityColor === "orange"
                              ? "bg-yellow-100 text-yellow-800"
                              : lead.priorityColor === "green"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {safeRender(lead.displayPriority)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        <div className="flex items-center">
                          <span className="mr-2">{safeRender(lead.score)}</span>
                          {lead.urgencyFlags?.needsAttention && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {safeRender(lead.formattedSavings)}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${
                            lead.statusColor === "green"
                              ? "bg-green-100 text-green-800"
                              : lead.statusColor === "yellow"
                              ? "bg-yellow-100 text-yellow-800"
                              : lead.statusColor === "red"
                              ? "bg-red-100 text-red-800"
                              : lead.statusColor === "gray"
                              ? "bg-gray-100 text-gray-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {getDealStatusLabel(lead.dealStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {safeRender(lead.daysSinceCreated)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Lead Scoring Section */}
      {Array.isArray(data.leadScoring) && data.leadScoring.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Top Priority Leads</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Company
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Score
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Potential Savings
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Urgency
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.leadScoring.slice(0, 10).map((lead, idx) => (
                  <tr
                    key={idx}
                    className={lead.score >= 70 ? "bg-yellow-50" : ""}
                  >
                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                      {safeRender(lead.name)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(lead.company)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.score >= 70
                            ? "bg-green-100 text-green-800"
                            : lead.score >= 50
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {safeRender(lead.score)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lead.totalSavings
                        ? `$${lead.totalSavings.toLocaleString()}`
                        : "-"}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.urgencyLevel === "High"
                            ? "bg-red-100 text-red-800"
                            : lead.urgencyLevel === "Medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {safeRender(lead.urgencyLevel)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.dealStatus === "won" ||
                          lead.dealStatus === "closed won"
                            ? "bg-green-100 text-green-800"
                            : lead.dealStatus === "open"
                            ? "bg-blue-100 text-blue-800"
                            : lead.dealStatus === "contacted"
                            ? "bg-yellow-100 text-yellow-800"
                            : lead.dealStatus === "interested"
                            ? "bg-orange-100 text-orange-800"
                            : lead.dealStatus === "quoted"
                            ? "bg-purple-100 text-purple-800"
                            : lead.dealStatus === "lost" ||
                              lead.dealStatus === "closed lost"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {getDealStatusLabel(lead.dealStatus)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Time Trends Section */}
      {Array.isArray(data.timeTrends) && data.timeTrends.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Time Trends</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Month
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Total Leads
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Contacted
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Converted
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Potential Revenue
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Contact Rate
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.timeTrends.map((trend, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(trend._id)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(trend.totalLeads)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(trend.contactedLeads)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(trend.convertedLeads)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {trend.potentialRevenue
                        ? `$${(trend.potentialRevenue / 1000000).toFixed(2)}M`
                        : "-"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {trend.contactRate
                        ? `${trend.contactRate.toFixed(1)}%`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Industry Analysis Section */}
      {data.industryAnalysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Brand Analysis */}
          {Array.isArray(data.industryAnalysis.brandAnalysis) &&
            data.industryAnalysis.brandAnalysis.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Brand Performance</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Brand
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Leads
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Avg Savings
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.industryAnalysis.brandAnalysis.map((brand, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-700 font-medium">
                            {safeRender(brand._id)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {safeRender(brand.totalLeads)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {brand.avgSavings
                              ? `$${brand.avgSavings.toLocaleString()}`
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Issue Analysis */}
          {Array.isArray(data.industryAnalysis.issueAnalysis) &&
            data.industryAnalysis.issueAnalysis.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Common Issues</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Issue
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Count
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Urgent
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.industryAnalysis.issueAnalysis.map((issue, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-700 font-medium">
                            {safeRender(issue._id)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {safeRender(issue.count)}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {issue.urgentCases > 0 && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {issue.urgentCases}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      )}

      {/* Comparison Data Section */}
      {Array.isArray(data.comparisonData) && data.comparisonData.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Comparison Data</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Brand Make
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Downtime
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Hours Got It
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Loss
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.comparisonData.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item._id)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item.totalDowntime)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item.totalHoursGotIt)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item.totalLoss)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Distribution Section */}
      {Array.isArray(data.distribution) && data.distribution.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Distribution</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Issue
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Count
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.distribution.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item._id)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {safeRender(item.count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Maintenance Section */}
      {data.maintenance && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Maintenance</h2>
          <div className="mb-4">
            <h3 className="text-lg font-semibold">Rebuild Stats</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Rebuild On Site
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data.maintenance.rebuildStats || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {item._id === true
                        ? "Yes"
                        : item._id === false
                        ? "No"
                        : safeRender(item._id)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(item.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 className="text-lg font-semibold">Who Rebuild Stats</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Who Rebuild
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data.maintenance.whoRebuildStats || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(item._id)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {safeRender(item.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lead Funnel Section */}
      {data.leadFunnel && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Sales Pipeline</h2>

          {/* Pipeline Status */}
          {Array.isArray(data.leadFunnel.funnelData) &&
            data.leadFunnel.funnelData.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Pipeline Status</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 mb-4">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Count
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Total Value
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Avg Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.leadFunnel.funnelData.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-700 font-medium">
                            {safeRender(item._id) === "-"
                              ? "Not Contacted"
                              : safeRender(item._id)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {safeRender(item.count)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {item.totalValue
                              ? `$${item.totalValue.toLocaleString()}`
                              : "-"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {item.avgValue
                              ? `$${item.avgValue.toLocaleString()}`
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Conversion Rates */}
          {data.leadFunnel.conversionRates && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Conversion Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">
                    Overall Rate
                  </h4>
                  <p className="text-xl font-bold text-gray-900">
                    {data.leadFunnel.conversionRates.overall
                      ? `${data.leadFunnel.conversionRates.overall}%`
                      : "0%"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">
                    Pending Leads
                  </h4>
                  <p className="text-xl font-bold text-gray-900">
                    {safeRender(data.leadFunnel.conversionRates.pending)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600">
                    Success Value
                  </h4>
                  <p className="text-xl font-bold text-gray-900">
                    {data.leadFunnel.conversionRates.successValue
                      ? `$${data.leadFunnel.conversionRates.successValue.toLocaleString()}`
                      : "$0"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Conversion Analysis */}
          {data.leadFunnel.conversionAnalysis &&
            Array.isArray(data.leadFunnel.conversionAnalysis) &&
            data.leadFunnel.conversionAnalysis.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">
                  Monthly Conversion Trends
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Month
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Conversions
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.leadFunnel.conversionAnalysis.map((conv, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {conv._id?.month || safeRender(conv._id)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {safeRender(conv.conversions)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {conv.revenue
                              ? `$${conv.revenue.toLocaleString()}`
                              : "$0"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      )}

      {/* Sales Pipeline & Recent Activity */}
      {data.salesPipeline && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Recent Activity */}
          {Array.isArray(data.salesPipeline.recentActivity) &&
            data.salesPipeline.recentActivity.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {data.salesPipeline.recentActivity
                    .slice(0, 8)
                    .map((activity, idx) => (
                      <div
                        key={idx}
                        className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded-r"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">
                              {activity.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {activity.email}
                            </p>
                            <p className="text-sm text-green-600 font-medium">
                              {activity.totalSavings
                                ? `$${activity.totalSavings.toLocaleString()}`
                                : "No savings calculated"}
                            </p>
                          </div>
                          <div className="text-right">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                activity.dealStatus === "won" ||
                                activity.dealStatus === "closed won"
                                  ? "bg-green-100 text-green-800"
                                  : activity.dealStatus === "open"
                                  ? "bg-blue-100 text-blue-800"
                                  : activity.dealStatus === "contacted"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : activity.dealStatus === "interested"
                                  ? "bg-orange-100 text-orange-800"
                                  : activity.dealStatus === "quoted"
                                  ? "bg-purple-100 text-purple-800"
                                  : activity.dealStatus === "lost" ||
                                    activity.dealStatus === "closed lost"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {getDealStatusLabel(activity.dealStatus)}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(
                                activity.createdAt
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* Pipeline Status Summary */}
          {Array.isArray(data.salesPipeline.pipelineStatus) &&
            data.salesPipeline.pipelineStatus.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Pipeline Summary</h2>
                <div className="space-y-4">
                  {data.salesPipeline.pipelineStatus.map((status, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <h3 className="font-medium text-gray-900">
                          {status._id || "Not Contacted"}
                        </h3>
                        <span className="text-lg font-bold text-gray-900">
                          {status.count}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>
                          Avg Savings:{" "}
                          {status.avgSavings
                            ? `$${status.avgSavings.toLocaleString()}`
                            : "$0"}
                        </p>
                        <p>
                          Total Potential:{" "}
                          {status.totalPotential
                            ? `$${status.totalPotential.toLocaleString()}`
                            : "$0"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Follow-up Needed */}
          {Array.isArray(data.salesPipeline.followUpNeeded) &&
            data.salesPipeline.followUpNeeded.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Follow-up Required</h2>
                <div className="space-y-3">
                  {data.salesPipeline.followUpNeeded.map((followUp, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-4 ${
                        followUp._id === "High"
                          ? "bg-red-50 border border-red-200"
                          : followUp._id === "Medium"
                          ? "bg-yellow-50 border border-yellow-200"
                          : "bg-green-50 border border-green-200"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            followUp._id === "High"
                              ? "bg-red-100 text-red-800"
                              : followUp._id === "Medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {followUp._id} Priority
                        </span>
                        <span className="text-lg font-bold">
                          {followUp.count} leads
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Avg waiting:{" "}
                        {followUp.avgDaysWaiting
                          ? `${followUp.avgDaysWaiting.toFixed(1)} days`
                          : "N/A"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* User Type Analysis & Engagement Metrics */}
      {data.industryAnalysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* User Type Analysis */}
          {Array.isArray(data.industryAnalysis.userTypeAnalysis) &&
            data.industryAnalysis.userTypeAnalysis.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">
                  User Type Performance
                </h2>
                <div className="space-y-3">
                  {data.industryAnalysis.userTypeAnalysis.map(
                    (userType, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-medium text-gray-900">
                            {userType._id}
                          </h3>
                          <span className="text-lg font-bold text-gray-900">
                            {userType.count}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${userType.conversionRate * 100}%`,
                            }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Conversion:{" "}
                          {(userType.conversionRate * 100).toFixed(1)}%
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

          {/* Engagement Metrics */}
          {data.leadQualityAnalysis &&
            Array.isArray(data.leadQualityAnalysis.engagementMetrics) &&
            data.leadQualityAnalysis.engagementMetrics.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-xl font-bold mb-4">Lead Engagement</h2>
                {data.leadQualityAnalysis.engagementMetrics.map(
                  (engagement, idx) => (
                    <div key={idx} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-blue-800">
                            New Material Interest
                          </h4>
                          <p className="text-xl font-bold text-blue-900">
                            {engagement.interestedInNewMaterial || 0}
                          </p>
                          <p className="text-xs text-blue-600">
                            {(
                              (engagement.interestedInNewMaterial /
                                engagement.totalLeads) *
                              100
                            ).toFixed(1)}
                            % of leads
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-green-800">
                            Expert Opinion
                          </h4>
                          <p className="text-xl font-bold text-green-900">
                            {engagement.needExpertOpinion || 0}
                          </p>
                          <p className="text-xs text-green-600">
                            {(
                              (engagement.needExpertOpinion /
                                engagement.totalLeads) *
                              100
                            ).toFixed(1)}
                            % of leads
                          </p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-purple-800">
                            New Applications
                          </h4>
                          <p className="text-xl font-bold text-purple-900">
                            {engagement.openToNewApplication || 0}
                          </p>
                          <p className="text-xs text-purple-600">
                            {(
                              (engagement.openToNewApplication /
                                engagement.totalLeads) *
                              100
                            ).toFixed(1)}
                            % of leads
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-gray-800">
                            Total Leads
                          </h4>
                          <p className="text-xl font-bold text-gray-900">
                            {engagement.totalLeads || 0}
                          </p>
                          <p className="text-xs text-gray-600">Overall count</p>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
        </div>
      )}

      {/* Actionable Insights Section */}
      {data.insights && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-xl font-bold mb-4">Key Insights & Performance</h2>

          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800">
                Conversion Rate
              </h4>
              <p className="text-2xl font-bold text-blue-900">
                {data.insights.conversionRate
                  ? `${data.insights.conversionRate}%`
                  : "0%"}
              </p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-800">
                Avg Deal Size
              </h4>
              <p className="text-2xl font-bold text-green-900">
                {data.insights.avgDealSize
                  ? `$${data.insights.avgDealSize.toLocaleString()}`
                  : "$0"}
              </p>
            </div>
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-800">
                Response Time
              </h4>
              <p className="text-2xl font-bold text-yellow-900">
                {data.insights.responseTime
                  ? `${data.insights.responseTime} days`
                  : "N/A"}
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-800">Hot Leads</h4>
              <p className="text-2xl font-bold text-purple-900">
                {data.insights.hotLeadsPercent
                  ? `${data.insights.hotLeadsPercent}%`
                  : "0%"}
              </p>
            </div>
          </div>

          {/* Top Priority Leads */}
          {Array.isArray(data.insights.topPriorityLeads) &&
            data.insights.topPriorityLeads.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Top Priority Leads (Action Required)
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.insights.topPriorityLeads.map((lead, idx) => (
                    <div
                      key={idx}
                      className="border border-orange-200 bg-orange-50 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {lead.name}
                          </h4>
                          <p className="text-sm text-gray-600">{lead.email}</p>
                          <p className="text-sm text-gray-600">
                            {lead.company}
                          </p>
                        </div>
                        <div className="text-right">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              lead.score >= 80
                                ? "bg-red-100 text-red-800"
                                : lead.score >= 60
                                ? "bg-orange-100 text-orange-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            Score: {lead.score}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Potential:</span>
                          <p className="font-medium text-green-600">
                            $
                            {lead.totalSavings
                              ? lead.totalSavings.toLocaleString()
                              : "0"}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Urgency:</span>
                          <p
                            className={`font-medium ${
                              lead.urgencyLevel === "High"
                                ? "text-red-600"
                                : lead.urgencyLevel === "Medium"
                                ? "text-orange-600"
                                : "text-green-600"
                            }`}
                          >
                            {lead.urgencyLevel}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Lost Leads Analysis */}
      {data.leadFunnel &&
        Array.isArray(data.leadFunnel.lostLeadsAnalysis) &&
        data.leadFunnel.lostLeadsAnalysis.length > 0 && (
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <h2 className="text-xl font-bold mb-4">Lost Leads Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.leadFunnel.lostLeadsAnalysis.map((lost, idx) => (
                <div
                  key={idx}
                  className="bg-red-50 border border-red-200 rounded-lg p-4"
                >
                  <h3 className="font-medium text-red-900 mb-2">{lost._id}</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-red-700">Lost Count:</span>{" "}
                      {lost.count}
                    </p>
                    <p>
                      <span className="text-red-700">Lost Value:</span> $
                      {lost.lostValue ? lost.lostValue.toLocaleString() : "0"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>💡 Insight:</strong> Focus on addressing these common
                issues to reduce lead loss and improve conversion rates.
              </p>
            </div>
          </div>
        )}

      {/* Lead Quality Analysis */}
      {data.leadQualityAnalysis &&
        Array.isArray(data.leadQualityAnalysis.qualityDistribution) &&
        data.leadQualityAnalysis.qualityDistribution.length > 0 && (
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <h2 className="text-xl font-bold mb-4">
              Lead Quality Distribution
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.leadQualityAnalysis.qualityDistribution.map(
                (quality, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">
                      {quality._id}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium">Count:</span>{" "}
                        {quality.count}
                      </p>
                      <p>
                        <span className="font-medium">Conversion:</span>{" "}
                        {quality.conversionRate}%
                      </p>
                      <p>
                        <span className="font-medium">Avg Revenue:</span>{" "}
                        {quality.avgRevenue
                          ? `$${quality.avgRevenue.toLocaleString()}`
                          : "$0"}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
    </div>
  );
};

function HomePage() {
  const reduxSessionId = useSelector((state) => state.auth.sessionId);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      // Get sessionId from Redux or localStorage
      const sessionId = reduxSessionId || localStorage.getItem("sessionId");

      if (!sessionId) {
        setLoading(false);
        setError("No session found");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await api.getDashboard();
        setDashboardData(data);
      } catch (error) {
        console.error("Dashboard API error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [reduxSessionId, isAuthenticated]); // Depend on both Redux sessionId and auth state

  if (loading) {
    return <Loading currentPage={"/home"} />;
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <NavBar currentPage={"/home"} />
        <div className="flex-grow flex items-center justify-center">
          <p className="text-lg font-medium text-red-500">
            Error loading dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavBar currentPage={"/home"} />
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <DashboardErrorBoundary>
          <Dashboard data={dashboardData} />
        </DashboardErrorBoundary>
      </div>
    </div>
  );
}

export default HomePage;
